import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import { Download } from 'lucide-react';
import Discover from '../Discover';

const SharedDiscover = () => {
  const { shareId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [allowDownload, setAllowDownload] = useState(false);

  useEffect(() => {
    fetchShareLink();
  }, [shareId]);

  const fetchShareLink = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${process.env.REACT_APP_NODE_URL}/discover/share-link/${shareId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      const data = await response.json();

      if (!response.ok || !data.status) {
        if (response.status === 410) {
          throw new Error('This share link has expired.');
        } else if (response.status === 404) {
          throw new Error('Share link not found.');
        } else {
          throw new Error(data.message || 'Failed to load share link');
        }
      }

      setShareData(data.discoverState);
      setAllowDownload(data.allowDownload === true);
    } catch (err) {
      console.error('Error fetching share link:', err);
      setError(err.message || 'Failed to load shared Discover page');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        <CircularProgress sx={{ color: '#39585C' }} />
        <Typography sx={{ color: '#6B7280' }}>Loading shared Discover page...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          padding: '2rem',
        }}
      >
        <Alert severity='error' sx={{ maxWidth: '600px' }}>
          <Typography variant='h6' sx={{ marginBottom: '0.5rem' }}>
            Unable to Load Shared Page
          </Typography>
          <Typography>{error}</Typography>
        </Alert>
      </Box>
    );
  }

  if (!shareData) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          padding: '2rem',
        }}
      >
        <Alert severity='warning' sx={{ maxWidth: '600px' }}>
          No data available for this share link.
        </Alert>
      </Box>
    );
  }

  return (
    <SharedDiscoverContent
      shareData={shareData}
      allowDownload={allowDownload}
    />
  );
};

const SharedDiscoverContent = ({ shareData, allowDownload }) => {
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  const handleExportPdf = async () => {
    if (!shareData) {
      setPdfError('No data available for PDF export');
      return;
    }

    setPdfGenerating(true);
    setPdfError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODE_URL}/discover/pdf/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            discoverState: shareData,
          }),
        },
      );

      const data = await response.json();

      if (!response.ok || !data.status) {
        throw new Error(data.message || 'Failed to generate PDF');
      }

      if (data.link) {
        window.open(data.link, '_blank');
      } else {
        throw new Error('PDF link not received');
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      setPdfError(err.message || 'Failed to generate PDF report');
    } finally {
      setPdfGenerating(false);
    }
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100vh' }}>
      {pdfError && (
        <Box
          sx={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            zIndex: 1000,
          }}
        >
          <Alert severity='error' sx={{ mb: 1 }}>
            {pdfError}
          </Alert>
        </Box>
      )}
      <Discover
        sharedMode={true}
        sharedState={shareData}
        allowDownload={allowDownload}
        onExportPdf={handleExportPdf}
        pdfGenerating={pdfGenerating}
      />
    </Box>
  );
};

export default SharedDiscover;

